import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const THEME_STORAGE_KEY = 'hisab_theme_prefs';

const DEFAULTS = { theme: 'classic', mode: 'light' };

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(THEME_STORAGE_KEY);
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { ...DEFAULTS };
  });

  const { theme, mode } = prefs;

  // Apply theme classes to document root so ALL elements are covered
  useEffect(() => {
    const root = document.documentElement;
    // Snapshot to array first — mutating classList during forEach skips items
    const toRemove = Array.from(root.classList).filter(cls => cls.startsWith('theme-'));
    toRemove.forEach(cls => root.classList.remove(cls));
    // Add base + variant
    root.classList.add(`theme-${theme}`);
    if (mode === 'dark') {
      root.classList.add(`theme-${theme}-dark`);
    }
  }, [theme, mode]);

  const save = useCallback((partial) => {
    setPrefs(prev => {
      const next = { ...prev, ...partial };
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setTheme = useCallback((t) => save({ theme: t }), [save]);
  const toggleDarkMode = useCallback(() => {
    save({ mode: mode === 'dark' ? 'light' : 'dark' });
  }, [mode, save]);
  const isDark = mode === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, mode, isDark, setTheme, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
